import React, { useMemo, useState, useRef } from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6"
import { MdTimer } from "react-icons/md"
import { Calendar, momentLocalizer } from "react-big-calendar"
import moment from "moment-timezone"
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"
import Swal from "sweetalert2"
import WorkoutModal from "pages/Workouts/addWorkout"
import { FetchPutData } from "utils/Common_api_functions"
import { Table } from "reactstrap"
import "moment/locale/en-gb"

moment.tz.setDefault("UTC")
moment.updateLocale("en-gb", {
  week: { dow: 1 }, // Monday = 1, Sunday = 0
})
const DnDCalendar = withDragAndDrop(Calendar)

const localizer = momentLocalizer(moment)

const TrainingPlanCalendar = ({
  selectedPlan,
  getDraggedEvent,
  handleSelectEvent,
  activities,
  handleDropFromOutside,
  onEventResize,
  handleDeleteEvent,
  setselectedActivity,
  onEventDrop,
  setLoading,
  setEvents,
  setSelectedPlan,
  setTrainingPlans,
}) => {
  const virtualStartDate = useMemo(() => {
    const startOfMonth = moment().startOf("month")
    // Go back to the Monday of that week (even if it's in the previous month)
    const firstMonday = startOfMonth.startOf("isoWeek")
    return firstMonday.toDate()
  }, [])

  const firstDayOfCurrentMonth = useMemo(
    () => moment().startOf("month").toDate(),
    []
  )

  const [currentStartDate, setCurrentStartDate] = useState(
    firstDayOfCurrentMonth
  )

  const duration = Number(selectedPlan?.duration) || 1
  const type = selectedPlan?.type || "day" // "day", "week", "month"

  // ✅ Total days calculation
  const totalDays = useMemo(() => {
    if (type === "week") return duration * 7
    if (type === "month") return duration * 30
    return duration
  }, [duration, type])

  // ✅ Generate plan periods
  const planPeriods = useMemo(() => {
    return [...Array(totalDays)].map((_, i) => {
      const date = moment(virtualStartDate).add(i, "days").toDate()
      const label =
        type === "week"
          ? `Week ${Math.floor(i / 7) + 1}`
          : type === "month"
          ? `Month ${Math.floor(i / 30) + 1}`
          : `Day ${i + 1}`
      return { date, dayIndex: i + 1, label }
    })
  }, [virtualStartDate, totalDays, type])

  // ✅ Only show dates within range
  const isDateInPlan = date => {
    const planStart = moment.utc(virtualStartDate).startOf("day")
    const planEnd = moment
      .utc(virtualStartDate)
      .add(totalDays - 1, "days")
      .endOf("day")

    const mDate = moment.utc(date)

    // Allow if the date is within the start and last day (ignore time)
    const isWithin =
      mDate.isSameOrAfter(planStart, "day") &&
      mDate.isSameOrBefore(planEnd, "day")
    return isWithin
  }

  const dayPropGetter = date => {
    if (isDateInPlan(date)) {
      return {
        className: "plan-day", // ✅ custom CSS class
        style: { backgroundColor: "#fff", cursor: "pointer" },
      }
    }

    return {
      className: "blocked-day", // ✅ another class for blocked cells
      style: {
        backgroundColor: "#dadada",
        cursor: "not-allowed",
        pointerEvents: "none",
        color: "#ccc",
      },
    }
  }

  // ✅ Normalize events from dayNumber → actual calendar date
  const normalizedEvents = useMemo(() => {
    if (!selectedPlan?.events || !planPeriods.length) return []

    return selectedPlan.events
      .map(ev => {
        if (!ev.dayNumber || ev.dayNumber.length === 0) return null

        const dayNumbers = (
          Array.isArray(ev.dayNumber) ? ev.dayNumber : [ev.dayNumber]
        ).map(n => Math.abs(Number(n)))

        const startPeriod = planPeriods.find(
          p => p.dayIndex === Math.min(...dayNumbers)
        )
        const endPeriod = planPeriods.find(
          p => p.dayIndex === Math.max(...dayNumbers)
        )

        if (!startPeriod || !endPeriod) return null

        let start = moment(startPeriod.date)
        let end = moment(endPeriod.date)

        if (ev.start) {
          const s = moment(ev.start)
          start = start.set({
            hour: s.hour(),
            minute: s.minute(),
            second: s.second(),
          })
        }

        if (ev.end) {
          const e = moment(ev.end)
          end = end.set({
            hour: e.hour(),
            minute: e.minute(),
            second: e.second(),
          })
        }

        // FIX multi-day display
        if (start.startOf("day").isBefore(end.startOf("day"))) {
          end = end.add(1, "day") // extend for rbc multi-day
        }

        return {
          ...ev,
          start: start.toDate(),
          end: end.toDate(),
          allDay: false,
          title: ev.title || ev.workout?.activityTitle || "Untitled Workout",
        }
      })
      .filter(Boolean)
  }, [selectedPlan, planPeriods])

  // ✅ Custom Toolbar (Month Navigation)

  const maxPeriods = Math.ceil(totalDays / 30)
  const currentPeriodIndex = Math.floor(
    moment(currentStartDate).diff(moment(firstDayOfCurrentMonth), "days") / 30
  )
  const isNextDisabled = currentPeriodIndex >= maxPeriods - 1
  const isPrevDisabled = moment(currentStartDate).isSameOrBefore(
    moment(firstDayOfCurrentMonth),
    "month"
  )

  const handleNavigate = action => {
    if (action === "PREV" && !isPrevDisabled) {
      const newDate = moment(currentStartDate).subtract(1, "month")
      if (newDate.isBefore(moment(virtualStartDate), "month")) return // 🚫 block before start
      setCurrentStartDate(newDate.toDate())
    } else if (action === "NEXT" && !isNextDisabled) {
      setCurrentStartDate(moment(currentStartDate).add(1, "month").toDate())
    }
  }

  const getCurrentPeriodLabel = () =>
    `${
      type === "day" ? "Days" : type === "week" ? "Weeks" : "Months"
    } Training Plan (${duration} ${type}s)`

  const [userworkoutmodal, setUserWorkoutModal] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [selectedDayNumber, setSelectedDayNumber] = useState(null)

  const handleSelectSlot = slotInfo => {
    console.log(slotInfo.start)
    if (!isDateInPlan(slotInfo.start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "You cannot add events outside your plan range!",
      })
      return
    }

    const selectedPeriod = planPeriods.find(p =>
      moment(p.date).isSame(slotInfo.start, "day")
    )
    setSelectedDayNumber(selectedPeriod?.dayIndex)
    setStartDate(slotInfo.start)
    setselectedActivity(null)
    setUserWorkoutModal(true)
  }

  const draggedWorkoutRef = useRef(null)

  // ✅ Add Workout Modal Save
  const handleCreateWorkoutforuser = async workoutData => {
    const newEvent = {
      title: workoutData.activityTitle || "Untitled Workout",
      start: startDate,
      end: startDate,
      allDay: true,
      workout: workoutData,
      dayNumber: [selectedDayNumber],
    }

    setEvents(prev => [...prev, newEvent])

    try {
      const APIdata = await FetchPutData(
        setLoading,
        "training/plan/insert/workout",
        selectedPlan._id,
        { event: newEvent }
      )

      if (!APIdata?.data?.success) throw new Error(APIdata?.res?.message)

      const updatedEvents =
        APIdata?.data?.events || APIdata?.data?.workoutCalender || []
      const normalized = updatedEvents.map(ev => ({
        ...ev,
        title: ev.title || ev.workout?.activityTitle || "Untitled Workout",
        start: new Date(ev.start),
        end: new Date(ev.end),
      }))

      setSelectedPlan(prev => ({ ...prev, events: normalized }))
      setTrainingPlans(prev =>
        prev.map(p =>
          p._id === selectedPlan._id ? { ...p, events: normalized } : p
        )
      )
    } catch (err) {
      Swal.fire({
        title: "Error",
        icon: "error",
        text: err.message || "Failed to add workout",
      })
      setEvents(prev => prev.filter(ev => ev !== newEvent))
    }

    draggedWorkoutRef.current = null
  }

  // ✅ Drag/Drop validation
  const handleDropFromOutsideValidated = args => {
    if (!isDateInPlan(args.start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "You cannot drop events outside your plan!",
      })
      return
    }

    const droppedDate = moment(args.start).startOf("day")
    const matchedPeriod = planPeriods.find(p =>
      moment(p.date).isSame(droppedDate, "day")
    )

    handleDropFromOutside?.({
      ...args,
      start: args.start,
      end: args.end || args.start,
      dayNumber: matchedPeriod?.dayIndex,
    })
  }

  const onEventDropValidated = args => {
    const { event, start, end } = args
    console.log("Event Drop", args)

    // Only strictly check the start date
    if (!isDateInPlan(start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "Cannot move outside plan!",
      })
      return
    }

    // Optional: adjust end date if it spills beyond plan
    const planEnd = moment(virtualStartDate)
      .add(totalDays - 1, "days")
      .endOf("day")
    const safeEnd = moment(end).isAfter(planEnd) ? planEnd.toDate() : end

    // ✅ Recalculate positive dayNumber
    const newDayNumber =
      moment(start).diff(moment(virtualStartDate), "days") + 1
    const safeDayNumber = Math.max(1, Math.min(totalDays, newDayNumber))

    const updatedEvent = {
      ...event,
      start,
      end: safeEnd,
      dayNumber: [safeDayNumber],
    }

    console.log(updatedEvent)

    onEventDrop?.({
      ...args,
      event: updatedEvent,
    })
  }

  const onEventResizeValidated = args => {
    console.log("Resize Drop", args)
    if (!isDateInPlan(args.start) || !isDateInPlan(args.end)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "Cannot resize outside plan!",
      })
      return
    }
    onEventResize?.(args)
  }

  // ✅ Custom date cell renderer to show "Day X" instead of actual date
  const CustomDateHeader = ({ label, date }) => {
    // Find the corresponding plan day
    const period = planPeriods.find(p => moment(p.date).isSame(date, "day"))
    const dayLabel = period ? `Day ${period.dayIndex}` : ""

    return (
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: period ? "#222" : "#aaa",
        }}
      >
        {dayLabel}
      </div>
    )
  }

  // ✅ Custom Toolbar
  const CustomToolbar = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 10,
        borderBottom: "2px solid #e0e0e0",
        backgroundColor: "#fafafa",
      }}
    >
      <div className="d-flex gap-2">
        <button
          onClick={() => handleNavigate("PREV")}
          disabled={isPrevDisabled}
          style={{
            padding: "5px 10px",
            cursor: isPrevDisabled ? "not-allowed" : "pointer",
            border: "1px solid #ddd",
            borderRadius: 4,
            backgroundColor: "#fff",
            opacity: isPrevDisabled ? 0.5 : 1,
          }}
        >
          <FaChevronLeft />
        </button>
        <button
          onClick={() => handleNavigate("NEXT")}
          disabled={isNextDisabled}
          style={{
            padding: "5px 10px",
            cursor: isNextDisabled ? "not-allowed" : "pointer",
            border: "1px solid #ddd",
            borderRadius: 4,
            backgroundColor: "#fff",
            opacity: isNextDisabled ? 0.5 : 1,
          }}
        >
          <FaChevronRight />
        </button>
      </div>
      <div style={{ fontWeight: "bold", fontSize: 14, color: "#666" }}>
        {getCurrentPeriodLabel()}
      </div>
      <div style={{ fontWeight: "bold", fontSize: 14, color: "#333" }}>
        {selectedPlan?.planName} Training Plan
      </div>
    </div>
  )

  // ✅ Custom Event Renderer
  const CustomEvent = ({ event }) => {
    return (
      <div style={{ padding: 2, margin: "0 2px", position: "relative" }}>
        <div className="fw-bold mb-1" style={{ fontSize: 11 }}>
          {event?.title}
        </div>
        <div style={{ fontSize: 11 }}>
          <MdTimer size={14} /> {event?.workout?.plannedDuration}
        </div>
        <Table className="mb-0 mt-2 text-white gap-2">
          {event?.workout?.buildWorkout?.map((item, index) => (
            <tr key={index}>
              <td style={{ fontSize: "12px" }} className="pe-2">
                {item?.typeOption
                  ? item.typeOption.charAt(0).toUpperCase()
                  : ""}
              </td>
              <td style={{ fontSize: "12px" }} className="pe-2">
                {item?.duration}
              </td>
              <td style={{ fontSize: "12px" }}>
                {item?.zone ? item.zone.replace(/zone/gi, "Z") : ""}
              </td>
            </tr>
          ))}
        </Table>
        <button
          onClick={e => {
            e.stopPropagation()
            handleDeleteEvent(event)
          }}
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            background: "transparent",
            border: "none",
            color: "red",
            cursor: "pointer",
            backgroundColor: "#fff",
            padding: 2,
          }}
        >
          ✕
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: 20, height: "100%" }}>
      <DnDCalendar
        localizer={localizer}
        events={normalizedEvents}
        views={["month"]}
        date={currentStartDate}
        onNavigate={() => {}}
        onEventDrop={onEventDropValidated}
        onEventResize={onEventResizeValidated}
        resizable
        selectable
        popup
        dragFromOutsideItem={getDraggedEvent}
        onDropFromOutside={handleDropFromOutsideValidated}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        dayPropGetter={dayPropGetter}
        components={{
          toolbar: CustomToolbar,
          event: CustomEvent,
          month: {
            dateHeader: CustomDateHeader, // ✅ Add this line
          },
        }}
        style={{ height: "100%" }}
      />

      <WorkoutModal
        isOpen={userworkoutmodal}
        toggle={() => setUserWorkoutModal(false)}
        onSave={handleCreateWorkoutforuser}
        startDate={startDate}
      />
    </div>
  )
}

export default TrainingPlanCalendar
